import { useState } from 'react';
import { X, Trash2, AlertTriangle } from 'lucide-react';
import { usePlatformOrganizations } from '../../../../hooks/platform-admin/usePlatformOrganizations';
import { Organization } from '../../../../services/platform-admin/platform-organizations.service';

interface DeleteOrganizationModalProps {
  organization: Organization;
  onClose: () => void;
  onSuccess: () => void;
}

export default function DeleteOrganizationModal({
  organization,
  onClose,
  onSuccess,
}: DeleteOrganizationModalProps) {
  const { deleteOrganization, isLoading, error } = usePlatformOrganizations();
  const [reason, setReason] = useState('');
  const [confirmName, setConfirmName] = useState('');
  const [localError, setLocalError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError('');

    if (!reason.trim()) {
      setLocalError('Reason is required');
      return;
    }

    if (confirmName !== organization.name) {
      setLocalError('Organization name does not match');
      return;
    }

    const result = await deleteOrganization(organization.id, { reason });

    if (result) {
      onSuccess();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-red-50">
          <h2 className="text-xl font-bold text-red-900 flex items-center gap-2">
            <Trash2 className="h-5 w-5" />
            Delete Organization
          </h2>
          <button
            onClick={onClose}
            className="text-red-900 hover:text-red-700 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Warning */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-red-800">
                <p className="font-medium mb-1">This is a destructive action:</p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Organization will be soft-deleted</li>
                  <li>Users will be unable to log in</li>
                  <li>All services will be disabled</li>
                  <li>Data can be restored within 30 days</li>
                  <li>After 30 days, data will be permanently deleted</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Organization Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-sm text-gray-600">Organization</div>
            <div className="text-lg font-semibold text-gray-900 mt-1">
              {organization.name}
            </div>
            <div className="text-sm text-gray-500 mt-1">
              {organization._count?.users || 0} users, {organization._count?.agents || 0} agents
            </div>
          </div>

          {/* Error Messages */}
          {(error || localError) && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
              {error || localError}
            </div>
          )}

          {/* Reason Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reason for Deletion *
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              placeholder="e.g., Customer requested account deletion"
              rows={3}
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              This reason will be logged in the audit trail
            </p>
          </div>

          {/* Confirmation Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type <span className="font-mono font-bold">{organization.name}</span> to confirm *
            </label>
            <input
              type="text"
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              placeholder={organization.name}
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Enter the organization name exactly as shown above
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || confirmName !== organization.name}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Deleting...' : 'Delete Organization'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
